import { IComboBox, IComboBoxOption } from '@fluentui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { ServiceFilterOption, ServiceFiltersResult } from '../types';

/**
 * Custom hook to manage service filter state (date-range-scoped)
 *
 * Manages the selection state for service filters. Filter options are provided
 * from external source (usePrefetchedFilterOptions) which extracts them from
 * already-fetched service usage data.
 *
 * The hook maintains two states:
 * - pendingServices: UI state that updates immediately on selection
 * - appliedServices: Committed state that triggers data fetches
 *
 * IMPORTANT: When date range changes, both pending and applied filters are automatically reset.
 * This is because service filters are scoped to the current date range.
 *
 * @param serviceOptions Array of service filter options (extracted from chart data)
 * @param startTime Start of the current time range (for change detection)
 * @param endTime End of the current time range (for change detection)
 * @param granularity Granularity of the current time range (for change detection)
 * @returns Object containing service options, pending/applied services, handlers, and computed states
 */
export function useServiceFilters(
  serviceOptions: ServiceFilterOption[],
  startTime: Date,
  endTime: Date,
  granularity: Granularity
): ServiceFiltersResult {
  // Pending state: immediate UI updates, no fetch triggered
  const [pendingServices, setPendingServices] = useState<string[]>([]);

  // Applied state: committed filters that trigger data fetches
  const [appliedServices, setAppliedServices] = useState<string[]>([]);

  // Track previous date range to detect changes
  const prevDateRangeRef = useRef<string>('');
  const currentDateRangeKey = `${startTime.toISOString()}-${endTime.toISOString()}-${granularity}`;

  // Auto-reset filters when date range changes
  useEffect(() => {
    if (prevDateRangeRef.current !== '' && prevDateRangeRef.current !== currentDateRangeKey) {
      setPendingServices([]);
      setAppliedServices([]);
    }
    prevDateRangeRef.current = currentDateRangeKey;
  }, [currentDateRangeKey]);

  // Handle service filter changes (updates pending state only, no fetch)
  const handleChange = useCallback(
    (_event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
      if (!option) return;

      setPendingServices((prev) => {
        const updatedServices = option.selected
          ? [...prev, option.key as string]
          : prev.filter((svc) => svc !== option.key);

        return updatedServices;
      });
    },
    [] // No dependencies - stable callback reference
  );

  // Apply handler: commits pending changes to applied state (triggers fetch)
  const handleApply = useCallback(() => {
    setAppliedServices(pendingServices);
  }, [pendingServices]);

  // Reset handler: implements two scenarios
  // 1. If no applied filters: clear pending only (no fetch)
  // 2. If applied filters exist: clear both pending and applied (triggers fetch)
  const handleReset = useCallback(() => {
    setPendingServices([]);

    // Only clear applied filters if there were any (triggers fetch only if needed)
    if (appliedServices.length > 0) {
      setAppliedServices([]);
    }
  }, [appliedServices]);

  // Computed values for button states
  const hasChanges = JSON.stringify([...pendingServices].sort()) !== JSON.stringify([...appliedServices].sort());
  const hasFiltersToReset = pendingServices.length > 0 || appliedServices.length > 0;

  return {
    serviceOptions,
    selectedServices: pendingServices,
    appliedServices,
    handleChange,
    handleApply,
    handleReset,
    hasChanges,
    hasFiltersToReset,
  };
}
